<template>
  <div
    id="app"
    class="leading-5 font-serif text-base overflow-y-scroll h-screen"
  >
    <transition name="fade-in">
      <menu-element v-if="loaded" />
    </transition>
    <transition name="fade-in">
      <div v-show="loaded" id="view-home">
        <header class="splash-page w-100 bg-white relative">
          <img
            src="./assets/images/splash_image.jpg"
            role="presentation"
            class="mx-auto"
          />
          <img
            src="./assets/images/outlined_people.png"
            role="presentation"
            class="people"
          />
          <div class="splash-text absolute top-0 right-0 bottom-0 left-0">
            <img
              v-if="locale === 'fr'"
              src="./assets/images/logo-fr.png"
              class="logo block w-25"
              role="presentation"
            />
            <img
              v-else
              src="./assets/images/logo.png"
              class="logo block"
              role="presentation"
            />
            <h1
              class="text-white text-center self-center lowercase"
              v-html="$t('i18nTitle')"
            />
            <span class="report-text text-blue h2 block lowercase">
              {{ $t("i18nDescription") }}
            </span>
          </div>
        </header>
        <main>
          <confronting-injustices-together class="pt-0 sm:pt-8 pb-5" />
          <speaking-their-truths class="pt-0 sm:pt-16 pb-16" />
          <taking-to-the-streets class="py-16" />
          <marking-milestones class="pt-16" />
          <organizing-youth-against-violence class="pt-0 pb-16" />
          <connecting-across-borders class="py-16" />
          <hightlighting-issues-for-election-2019 class="py-16" />
          <globalizing-equality-in-2019 class="py-16" />
          <div
            class="border border-white h-px w-full max-w-screen-lg mx-auto"
          />
          <recognizing-social-justice-activists class="py-16" />
          <celebrating-our-donor-community class="py-16" />
          <at-a-glance class="pt-16" />
          <honouringHumanRightsDefenders class="pt-0 sm:pt-16 pb-16" />
          <inter-pares-expenses-in-2019 class="py-16" />
        </main>
        <footer class="pt-16">
          <page-section
            section-name="footer"
            :content="$t('i18nSections.footer')"
            class="pb-16"
          >
            <template v-slot="{ content }">
              <p v-html="content.credit" class="pt-16" />
            </template>
            <template #footer>
              <div class="relative w-100 flex justify-end">
                <img
                  src="./assets/images/outlined_people-flipped.png"
                  class="people right-0 bottom-0 max-w-lg"
                  role="presentation"
                />
              </div>
            </template>
          </page-section>
          <div class="footer-image relative">
            <img
              src="./assets/images/splash_image-footer.jpg"
              role="presentation"
            />
            <div class="footer-text absolute inset-0 d-flex items-center pr-6">
              <div>
                <img
                  v-if="locale === 'fr'"
                  src="./assets/images/logo-fr.png"
                  class="logo block w-25"
                  role="presentation"
                />
                <img
                  v-else
                  src="./assets/images/logo.png"
                  class="logo block"
                  role="presentation"
                />
                <ul class="pt-6 text-blue">
                  <li
                    v-for="item in $t('i18nSections.footer.contact')"
                    :key="item"
                    v-html="item"
                    class="py-1"
                  />
                </ul>
                <social-menu class="my-6" />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </transition>
    <div
      v-if="!loaded"
      class="absolute inset-0 flex flex-col items-center justify-center"
    >
      <div class="loading-area flex flex-col items-center justify-center">
        <img src="./assets/images/logo-xs.png" role="presentation" />
        <span class="pt-4">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "App",
  components: {
    menuElement: () => import("@/components/Menu.vue"),
    confrontingInjusticesTogether: () =>
      import("@/views/ConfrontingInjusticesTogether.vue"),
    speakingTheirTruths: () => import("@/views/SpeakingTheirTruths.vue"),
    takingToTheStreets: () => import("@/views/TakingToTheStreets.vue"),
    markingMilestones: () => import("@/views/MarkingMilestones.vue"),
    organizingYouthAgainstViolence: () =>
      import("@/views/OrganizingYouthAgainstViolence.vue"),
    connectingAcrossBorders: () =>
      import("@/views/ConnectingAcrossBorders.vue"),
    hightlightingIssuesForElection2019: () =>
      import("@/views/HightlightingIssuesForElection2019.vue"),
    globalizingEqualityIn2019: () =>
      import("@/views/GlobalizingEqualityIn2019.vue"),
    recognizingSocialJusticeActivists: () =>
      import("@/views/RecognizingSocialJusticeActivists.vue"),
    celebratingOurDonorCommunity: () =>
      import("@/views/CelebratingOurDonorCommunity.vue"),
    atAGlance: () => import("@/views/AtAGlance.vue"),
    interParesExpensesIn2019: () =>
      import("@/views/InterParesExpensesIn2019.vue"),
    honouringHumanRightsDefenders: () =>
      import("@/views/HonouringHumanRightsDefenders.vue"),
    pageSection: () => import("@/components/PageSection.vue"),
    socialMenu: () => import("@/components/SocialMenu.vue"),
  },
  data() {
    return {
      loaded: false,
    };
  },
  mounted() {
    window.addEventListener("load", () => {
      this.loaded = true;
    });
  },
};
</script>

<style lang="scss">
@use "sass:map";

$heading-sizes: (
  1: "text-5xl",
  2: "text-4xl",
  3: "text-3xl",
  4: "text-2xl",
  5: "text-xl",
  6: "text-lg",
);

$heading-leading: (
  1: "leading-10",
  2: "leading-8",
  3: "leading-11",
  4: "leading-6",
  5: "leading-9",
  6: "leading-8",
);

html {
  background-image: url("./assets/images/paper_texture.jpg");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

#app {
  scroll-behavior: smooth;
  scroll-snap-type: y proximity;

  @for $level from 1 through 6 {
    h#{$level},
    .h#{$level} {
      @apply font-sans #{map.get($heading-sizes, $level)} #{map.get($heading-leading, $level)};

      font-weight: bold;

      strong {
        font-size: 125%;
      }

      small {
        display: inline-block;
        font-size: 75%;
        font-weight: normal;
        vertical-align: top;
      }
    }
  }

  h1,
  .h1 {
    @screen sm {
      @apply text-6xl leading-14;
    }

    @screen lg {
      @apply text-7xl leading-16;
    }

    @screen xl {
      @apply text-9xl leading-18;
    }
  }

  h2,
  .h2 {
    @screen lg {
      @apply text-6xl leading-14;
    }
  }

  h3,
  .h3 {
    @apply text-blue;

    position: relative;
    font-weight: normal;

    &::before {
      content: " ";
      display: block;
      position: absolute;
      left: -0.75rem;
      width: 0.25rem;
      height: 5rem;
      background-image: url("./assets/images/lead-line.svg");
      background-repeat: no-repeat;
      background-position: center;
      background-origin: content-box;
      background-size: 100%;
    }
  }

  h4,
  .h4 {
    @apply text-green;
  }

  p {
    &:not(:last-child) {
      margin-bottom: 1rem;
    }

    &.lead {
      @apply text-blue;

      position: relative;

      &::before {
        content: " ";
        display: block;
        position: absolute;
        left: -0.75rem;
        width: 0.25rem;
        height: 5rem;
        background-image: url("./assets/images/lead-line.svg");
        background-repeat: no-repeat;
        background-position: center;
        background-origin: content-box;
        background-size: 100%;
      }
    }
  }
}

.text-shadow {
  text-shadow: 0.1rem 0.1rem 0.2rem #000;
}

@keyframes pulse {
  from {
    transform: scale(1);
  }

  to {
    transform: scale(1.1);
  }
}

.loading-area {
  img {
    transition: all 0.5s ease;
    animation: 1s ease 0s infinite alternate pulse;
  }
}

.fade-in {
  &-enter-active {
    transition: all 1s ease;
  }

  &-enter {
    opacity: 0;
  }

  &-enter-to {
    opacity: 1;
  }
}

// Force accessible focus state.
* {
  box-sizing: content-box;

  &:focus {
    outline: 2px dashed #000 !important;
    outline-offset: -2px !important;
    box-shadow: 0 0 0 2px #fff inset !important;
  }
}

.splash-page {
  h1 {
    text-shadow: 0.1rem 0.1rem 0.2rem #000;
  }

  .splash-text {
    display: grid;
    grid-template-rows: 1fr 2fr 1fr;

    .logo {
      justify-self: end;
      align-self: center;
      max-width: 30%;
      padding-right: 1rem;

      @screen md {
        padding-right: 2rem;
      }

      @screen lg {
        padding-right: 4rem;
      }
    }

    .report-text {
      justify-self: center;
      align-self: center;
      text-shadow: 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff,
        -1px 1px 0 #fff;

      @screen md {
        justify-self: end;
        padding-right: 2rem;
      }

      @screen lg {
        padding-right: 4rem;
      }
    }
  }

  .people {
    position: absolute;
    bottom: 1rem;
    left: 1rem;
    width: 38%;

    @screen sm {
      bottom: 2rem;
      left: 2rem;
    }

    @screen lg {
      bottom: 3rem;
      left: 3rem;
    }
  }
}

.footer-image {
  > img {
    display: none;

    @screen lg {
      display: block;
    }
  }
}

.footer-text {
  display: grid;
  align-items: center;
  justify-items: center;

  a:not(.social-link) {
    @apply font-sans text-blue border-b-2 border-blue;

    &:hover,
    &:focus {
      @apply border-b-4 bg-blue-pale;
    }
  }

  @screen lg {
    grid-template-columns: 70% 30%;

    > div {
      grid-column-start: 2;
    }
  }
}
</style>
